import { ApolloCache } from '@apollo/client'
import { produce } from 'immer'
import { Project, Survey, Wave } from '../../../data/gql-gen/dashboard/graphql'
import { SearchProjects } from '../../../modules/Project/Project.query'

/**
 * Removes a market (Survey) from the cached project so the views that read it
 * reflect the removal without waiting on a refetch — notably the markets table
 * on the multi-market project page and the projects list (`searchProjects`).
 * This matters when a market is removed from somewhere other than the project
 * page (e.g. the per-market "Market settings" drawer, which then navigates back
 * to the — cache-first — project page).
 *
 * Surveys are embedded in the Project (no keyFields, see apolloClient config),
 * so the array is filtered directly. Idempotent (a no-op if the survey is
 * already absent) and scoped to the given `projectId`. Mirrors
 * `AddMarketCacheUpdate`.
 */
export const RemoveMarketCacheUpdate = (
  cache: ApolloCache<unknown>,
  projectId: string,
  removedSurveyId: string
): void => {
  const removeSurvey = (surveys: Survey[] = []): Survey[] =>
    surveys.filter((survey) => survey.surveyId !== removedSurveyId)

  cache.modify({
    fields: {
      // Wave.surveys are embedded copies, not references to the project's.
      waves(existing) {
        const waves = existing as Wave[] | undefined
        if (!waves?.length || waves[0].projectId !== projectId) {
          return existing
        }
        return produce(waves, (draft) => {
          for (const wave of draft) {
            wave.surveys = removeSurvey(wave.surveys)
          }
        })
      },
      // projectById (PROJECT_BY_ID — used by the multi-market pages).
      project(existing) {
        const project = existing as Project | undefined
        if (!project || project.projectId !== projectId) {
          return existing
        }
        return produce(project, (draft) => {
          draft.surveys = removeSurvey(draft.surveys)
        })
      },
      // Keyed per surveyId; runs for every cached entry. Entries for this
      // project share its surveys, so drop the removed one from each (incl. the
      // removed market's own entry and the master entry the questionnaire
      // dialog reads).
      projectBySurveyId(existing) {
        const project = existing as Project | undefined
        if (!project || project.projectId !== projectId) {
          return existing
        }
        return produce(project, (draft) => {
          draft.surveys = removeSurvey(draft.surveys)
        })
      },
      searchProjects(existing) {
        const searchProjects = existing as SearchProjects | undefined
        const index = searchProjects?.projects.findIndex(
          (project) => project.projectId === projectId
        )
        if (!searchProjects || index === undefined || index < 0) {
          return existing
        }
        return produce(searchProjects, (draft) => {
          draft.projects[index].surveys = removeSurvey(
            draft.projects[index].surveys
          )
        })
      }
    }
  })
}
